<template>
  <VTableRemote
    v-if="hasPermission('training.categories.view')"
    :iconHeader="hasPermission('training.categories.add') ? 'add' : null"
    :items="displayedVideoCategories"
    isSearchable
    hideDefaultFooter
    isCustomIcon
    :isInfinitScroll="true"
    :headers="tableHeaders"
    :itemActions="actions"
    :title="$t('administration.videoCategories')"
    :centerColumns="['action']"
    :sortableColumns="['name']"
    :notSortableColumns="['action']"
    :pagination="pagination"
    @iconAction="onClickAction"
    @click-icon-header="onClickAdd"
    @sendQueryParams="onQueryParams"
  >
    <template #withInfiniteScroll>
      <div v-intersect="endIntersect" />
    </template>
  </VTableRemote>
  <NoPermissionAlert
    v-else
    :message="$t('permissions.error')"
  />
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import NameForm from '@/components/forms/NameForm';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import perpage from '@/constants/pagination';

export default {
    components: {
        VTableRemote,
        NoPermissionAlert,
    },
    data() {
        return {
            videoCategories: [],
            loading: false,
            pagination: {},
            queryParams: {
                page: 1,
                perpage,
                search: '',
                pagination: true,
            },
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
            'teamMainLang',
        ]),
        tableHeaders() {
            return [
                'name',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
        },
        displayedVideoCategories() {
            return this.videoCategories.map(videoCategory => ({
                id: videoCategory.id,
                name: videoCategory.name[this.userLang] || videoCategory.name[this.teamMainLang],
            }));
        },
        actions() {
            const actions = [];
            if (this.hasPermission('training.categories.edit')) {
                actions.push({
                    name: this.$t('forms.labels.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('training.categories.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    mounted() {
        this.loading = true;
        this.getVideoCategories();
        this.loading = false;
    },
    methods: {
        async getVideoCategories() {
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            const { data } = await this.$rq.getVideoCategories(cleanQueryParams);
            this.videoCategories = [...this.videoCategories, ...data.categories];
            this.pagination = data.pagination;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('videoCategory.add'),
                component: Vue.component('NameForm', NameForm),
                showLangs: true,
                onConfirm: requestPayload => this.addVideoCategory(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getVideoCategory(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('videoCategory.edit'),
                    showLangs: true,
                    component: Vue.component('NameForm', NameForm),
                    payload: { name: cloneDeep(data), isEdition: true },
                    onConfirm: requestPayload => this.editVideoCategory(item.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('videoCategory.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('videoCategory.confirmRemove') },
                    onConfirm: () => this.removeVideoCategory(item),
                    isForm: false,
                });
            }
        },
        async addVideoCategory(requestPayload) {
            await this.$rq.addVideoCategory(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.videoCategoryCreated', { name: requestPayload.name[this.userLang] }),
            });
            await this.onQueryParams({});
        },
        async editVideoCategory(id, requestPayload) {
            if (requestPayload) {
                await this.$rq.editVideoCategory(id, requestPayload);
                this.$toasted.global.toast_success({
                    message: this.$t('msg.videoCategoryEdited', { name: requestPayload.name[this.userLang] }),
                });
            }
            await this.onQueryParams({});
        },
        async removeVideoCategory(category) {
            await this.$rq.removeVideoCategory(category.id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.videoCategoryDeleted', { name: category.name }),
            });
            await this.onQueryParams({});
        },
        onQueryParams(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                search: queryParams?.search || '',
                page: 1,
            };
            this.pagination = {};
            this.videoCategories = [];
            this.getVideoCategories();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getVideoCategories();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },

    },
};
</script>
